import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  LeftIcon,
  RightIcon,
  SearchIcon,
  TTranslator,
} from '@smartshell/ui-kit';

import { TLocalesKeys } from 'localization';
import { IShortcutGroup } from 'schemas';

import s from './ShellCatalogFilters.module.scss';
import { TShellTopPanelTab } from './ShellTopPanel';

interface IShellCatalogFiltersProps {
  tab: TShellTopPanelTab;
  groups: IShortcutGroup[];
  selectedGroupId: number | null;
  searchValue: string;
  t: TTranslator<TLocalesKeys>;
  onGroupChange: (groupId: number | null) => void;
  onSearchChange: (value: string) => void;
}

const SCROLL_STEP = 240;

export const ShellCatalogFilters: FC<IShellCatalogFiltersProps> = ({
  tab,
  groups,
  selectedGroupId,
  searchValue,
  t,
  onGroupChange,
  onSearchChange,
}) => {
  const groupsRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const sectionTitle = t(
    tab === 'games' ? 'shell.tabs.games' : 'shell.tabs.applications'
  );

  const updateScrollState = useCallback((): void => {
    const container = groupsRef.current;
    if (!container) return;

    const maximumScroll = container.scrollWidth - container.clientWidth;
    const overflowed = maximumScroll > 1;

    setHasOverflow(overflowed);
    setCanScrollLeft(overflowed && container.scrollLeft > 1);
    setCanScrollRight(overflowed && container.scrollLeft < maximumScroll - 1);
  }, []);

  useEffect(() => {
    updateScrollState();

    const container = groupsRef.current;
    if (!container) return undefined;

    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(updateScrollState);
    resizeObserver?.observe(container);
    window.addEventListener('resize', updateScrollState);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateScrollState);
    };
  }, [groups, updateScrollState]);

  const scrollGroups = (direction: -1 | 1): void => {
    groupsRef.current?.scrollBy({
      left: direction * SCROLL_STEP,
      behavior: 'smooth',
    });
  };

  const changeSearch = (event: ChangeEvent<HTMLInputElement>): void => {
    onSearchChange(event.target.value);
  };

  return (
    <section
      className={s.ssShellCatalogFilters}
      aria-label={sectionTitle}
      data-testid="ss-shell-catalog-filters"
    >
      <label className={s.ssShellCatalogSearch}>
        <SearchIcon color="currentColor" size="m" />
        <input
          type="search"
          value={searchValue}
          placeholder={`${t(
            'search.placeholder'
          )} ${sectionTitle.toLocaleLowerCase()}`}
          aria-label={`${t(
            'search.placeholder'
          )} ${sectionTitle.toLocaleLowerCase()}`}
          onChange={changeSearch}
        />
      </label>
      <div className={s.ssShellCatalogGroupsArea}>
        <div
          ref={groupsRef}
          className={s.ssShellCatalogGroups}
          role="group"
          aria-label={sectionTitle}
          onScroll={updateScrollState}
        >
          <button
            className={s.ssShellCatalogGroup}
            type="button"
            aria-pressed={selectedGroupId === null}
            onClick={() => onGroupChange(null)}
          >
            {`${t('all')} ${sectionTitle.toLocaleLowerCase()}`}
          </button>
          {groups.map((group) => (
            <button
              key={group.id}
              className={s.ssShellCatalogGroup}
              type="button"
              aria-pressed={selectedGroupId === group.id}
              onClick={() => onGroupChange(group.id)}
            >
              {group.title}
            </button>
          ))}
        </div>
        {hasOverflow && (
          <div className={s.ssShellCatalogControls}>
            <button
              className={s.ssShellCatalogControl}
              type="button"
              aria-label={t('left')}
              disabled={!canScrollLeft}
              onClick={() => scrollGroups(-1)}
            >
              <LeftIcon color="currentColor" size="l" />
            </button>
            <button
              className={s.ssShellCatalogControl}
              type="button"
              aria-label={t('right')}
              disabled={!canScrollRight}
              onClick={() => scrollGroups(1)}
            >
              <RightIcon color="currentColor" size="l" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
